<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ACCOUNT_CLOSURE_POSTING')"
            :label="$t('Post')"
            size="md"
            class="mt-0"
            @click="goToAddAccountClosure"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="accountClosureList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAccountClosure"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!--Product Name -->
        <b-col md="6">
          <InputField
            id="product-name"
            v-model="filter.productName"
            :label="$t('Product Name')"
            :placeholder="$t('Enter Product Name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Product Name -->

        <!-- Closure Date From -->
        <b-col md="6">
          <DatePickerField
            id="closure-date-from"
            v-model="filter.financialDateFrom"
            :label="$t('Closure Date (From)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Closure Date From-->

        <!-- Closure Date To -->
        <b-col md="6">
          <DatePickerField
            id="closure-date-to"
            v-model="filter.financialDateTo"
            :label="$t('Closure Date (To)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Closure Date To-->
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
          <!-- Branch -->
        </b-col>

        <b-col md="6">
          <SelectField
            id="closure-reason"
            v-model="filter.closureReason"
            :label="$t('Closure Reason')"
            :reduce="option => option.value"
            :options="accountClosureReasonOptions"
            item="label"
            placeholder="Select Closure Reason"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toAmount, toBalance } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { closureReasonOptions } from '@/helpers/data'
import { toDateWithYear, toDateWithTime } from '../../../../helpers/formatDate'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'dateCreated', sortable: true, label: 'Closure Date' },
        { key: 'amount', sortable: true, label: 'Available Balance' },
        { key: 'closureReason', sortable: true, label: 'Closure Reason' },
        { key: 'initiator', sortable: true, label: 'Closed By' },
      ],
      accountClosureList: [],
      accountClosureReasonOptions: closureReasonOptions,
      cashDepositData: {
        accountNumber: '',
        accountName: '',
        branch: '',
        financialDate: '',
        instrumentNumber: '',
        narration: '',
      },
      filter: {
        accountName: null,
        accountNumber: null,
        instrumentNumber: null,
        branchCode: null,
        financialDateFrom: null,
        financialDateTo: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      accountClosurePerPage: 10,
      accountClosurePageSize: 0,
      accountClosureCurrentPage: 1,
      accountClosureTotlCount: 0,
      showSpinner: false,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchAccountClosure()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchAccountClosure()
    },
  },
  mounted() {
    this.fetchAccountClosure()
  },
  methods: {
    ...mapActions({
      getAccountClosureReport: 'reports/getTransactionReports',
    }),
    goToAddAccountClosure() {
      this.$router.push('add-account-closure')
    },
    async fetchAccountClosure() {
      try {
        this.isBusy = true
        const response = await this.getAccountClosureReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          route: 'getAccountClosureHistory',
          permission: 'VIEW_ACCOUNT_CLOSURE_POSTING',
        })
        const { data } = response
        if (data.data !== null) {
          this.isBusy = false
          this.accountClosureList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAccountClosure()
    },
  },
}
</script>
